import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDocsetManifest } from "../../context/DocsetContext";
import type { DocNode } from "../../types/docset";
import clsx from "clsx";
import { ChevronDownIcon, ChevronRightIcon } from "../icons/Chevron";

type NavSidebarProps = {
  activeNode?: DocNode;
  onNavigate?: () => void;
};

export function NavSidebar({ activeNode, onNavigate }: NavSidebarProps) {
  const manifest = useDocsetManifest();
  const params = useParams();
  const docsetId = params.docsetId ?? manifest.id;
  const activePath = activeNode ? activeNode.pathSegments.join("/") : "";
  const accentColor = manifest.theme?.accentColor ?? "#1e88e5";

  // Track user-controlled expansion
  const [userExpanded, setUserExpanded] = useState<Set<string>>(() => new Set());

  // Base expansion derived from active path: ensure all ancestors are expanded
  const baseExpanded = useMemo(() => {
    const set = new Set<string>();
    if (!activePath) return set;
    const parts = activePath.split("/");
    for (let i = 1; i <= parts.length; i += 1) {
      set.add(parts.slice(0, i).join("/"));
    }
    return set;
  }, [activePath]);

  const isExpanded = (key: string) => baseExpanded.has(key) || userExpanded.has(key);
  const toggleExpanded = (key: string) => {
    setUserExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <nav className="flex flex-1 flex-col overflow-hidden">
      <div className="doc-nav-scroll flex-1 overflow-y-auto px-6 pb-6 pt-4">
        <ul className="space-y-2 list-none pl-0 m-0">
          {manifest.nav.map((node) => (
            <NavItem
              key={node.pathSegments.join("/")}
              node={node}
              docsetId={docsetId}
              activePath={activePath}
              depth={0}
              accentColor={accentColor}
              onNavigate={onNavigate}
              isExpanded={isExpanded}
              onToggle={toggleExpanded}
            />
          ))}
        </ul>
      </div>
    </nav>
  );
}

type NavItemProps = {
  node: DocNode;
  docsetId: string;
  activePath: string;
  depth: number;
  accentColor: string;
  onNavigate?: () => void;
  isExpanded: (key: string) => boolean;
  onToggle: (key: string) => void;
};

function NavItem({
  node,
  docsetId,
  activePath,
  depth,
  accentColor,
  onNavigate,
  isExpanded,
  onToggle
}: NavItemProps) {
  const pathKey = node.pathSegments.join("/");
  const isActive = pathKey === activePath;
  const paddingClasses = ["pl-0", "pl-2", "pl-3", "pl-4", "pl-5", "pl-6"];
  const slotPadding = paddingClasses[Math.min(depth, paddingClasses.length - 1)];
  const hasContent = Boolean(node.contentHref);
  // Relative link: resolves against the current `:docsetId/*` route, whatever base the SPA is mounted at
  const targetHref = pathKey ? `${pathKey}` : ``;
  const itemRef = useRef<HTMLAnchorElement | HTMLDivElement | HTMLButtonElement | null>(null);
  const expanded = isExpanded(pathKey);

  useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.scrollIntoView({ block: "center" });
    }
  }, [isActive]);

  return (
    <li>
      <div className={clsx("space-y-1 rounded-lg", slotPadding)}>
        <div className="flex items-center gap-0.5">
          {hasContent ? (
            <Link
              ref={(element) => {
                itemRef.current = element;
              }}
              to={targetHref}
              className={clsx(
                "flex min-w-0 flex-1 items-center gap-1 rounded-lg pl-0 pr-2 py-2 text-[13px] font-medium transition-all",
                !isActive && "text-slate-300 hover:bg-slate-800/50 hover:text-white"
              )}
              style={isActive ? { color: "#fff" } : undefined}
              onClick={onNavigate}
            >
              {isActive ? (
                <span
                  aria-hidden
                  className="inline-block h-4 w-[3px] rounded-full mr-2"
                  style={{ backgroundColor: accentColor }}
                />
              ) : (
                <span aria-hidden className="inline-block h-4 w-[3px] mr-2" />
              )}
              <span className="truncate">{node.title}</span>
            </Link>
          ) : (
            <div className="sdiv">
              <button
                ref={(element) => {
                  itemRef.current = element;
                }}
                type="button"
                aria-expanded={expanded}
                onClick={() => onToggle(pathKey)}
                className="flex min-w-0 flex-1 items-center gap-1 pl-0 pr-2 py-2 text-left text-xs font-semibold uppercase tracking-wide strt text-slate-500 hover:text-slate-300"
              >
                <span className="truncate">{node.title}</span>
              </button>
              <hr className="shr"></hr>
            </div>
          )}
          {node.children.length > 0 ? (
            <button
              type="button"
              aria-label={expanded ? "Collapse" : "Expand"}
              aria-expanded={expanded}
              onClick={() => onToggle(pathKey)}
              className="ml-1 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded hover:bg-slate-800/40"
            >
              {expanded ? (
                <ChevronDownIcon className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-slate-400" />
              )}
            </button>
          ) : null}
        </div>
        {node.children.length > 0 && expanded ? (
          <ul className={clsx("space-y-1 list-none border-l border-slate-800/60 pl-2")}>
            {node.children.map((child) => (
              <NavItem
                key={child.pathSegments.join("/")}
                node={child}
                docsetId={docsetId}
                activePath={activePath}
                depth={depth + 1}
                accentColor={accentColor}
                onNavigate={onNavigate}
                isExpanded={isExpanded}
                onToggle={onToggle}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}
